import React, { forwardRef } from 'react';

import './ProgressiveImage.css';
import { useProgressiveImage } from './useProgressiveImage';

/**
 * Drop-in <img>-like component that renders tiered variants of a
 * multi-resolution asset and cross-fades from blurry thumb -> preview
 * -> full as each tier finishes decoding.
 *
 * Accepts either:
 *   - `src`: a single URL (legacy path; behaves like a plain <img>)
 *   - `urls`: a `{ thumb, preview, full, width?, height? }` bundle
 *             produced by `battleplan.core.images.multi_res_urls`
 *
 * The `objectFit` / `objectPosition` props are forwarded to every
 * layer so the blurred placeholder lines up with the sharp image.
 *
 * The ref (if provided) is attached to the *full-res* <img>, matching
 * the behavior callers expect from a normal <img ref={...}>.
 */
const ProgressiveImage = forwardRef(function ProgressiveImage(
  {
    src = null,
    urls = null,
    alt = '',
    className = '',
    style = null,
    objectFit = 'contain',
    objectPosition = 'center',
    onLoad = null,
    onError = null,
    draggable = false,
  },
  ref,
) {
  // `urls` wins when both are supplied so callers can freely pass
  // `urls={...} src={legacyFallback}`.
  const source = urls || src;
  const { thumb, preview, full, previewLoaded, fullLoaded } =
    useProgressiveImage(source);

  const layerStyle = {
    objectFit,
    objectPosition,
  };

  return (
    <div className={`progressive-image ${className}`} style={style || undefined}>
      {thumb && (
        <img
          src={thumb}
          alt=""
          aria-hidden="true"
          className={[
            'progressive-image__layer',
            'progressive-image__thumb',
            fullLoaded || previewLoaded
              ? 'progressive-image__layer--hidden'
              : 'progressive-image__layer--visible',
          ].join(' ')}
          style={layerStyle}
          draggable={false}
        />
      )}
      {preview && (
        <img
          src={preview}
          alt=""
          aria-hidden={fullLoaded ? 'true' : 'false'}
          className={[
            'progressive-image__layer',
            'progressive-image__preview',
            previewLoaded && !fullLoaded
              ? 'progressive-image__layer--visible'
              : 'progressive-image__layer--hidden',
          ].join(' ')}
          style={layerStyle}
          draggable={false}
        />
      )}
      {full && (
        <img
          ref={ref}
          src={full}
          alt={alt}
          decoding="async"
          loading="eager"
          onLoad={onLoad}
          onError={onError}
          className={[
            'progressive-image__layer',
            'progressive-image__full',
            fullLoaded
              ? 'progressive-image__layer--visible'
              : 'progressive-image__layer--hidden',
          ].join(' ')}
          style={layerStyle}
          draggable={draggable}
        />
      )}
    </div>
  );
});

export default ProgressiveImage;
